import {
  FormArrayStructure,
  FormControlStructure,
  FormGroupStructure,
  FormStructure,
  FormStructureType,
} from './form-structure';

type StructureNode = FormControlStructure | FormGroupStructure | FormArrayStructure;

/** Suffix of the marker key the server reads a tag from: `<path>__pathKey`. */
export const PATH_KEY_MARKER_SUFFIX = '__pathKey';

export interface PathKeyMarker {
  /** Wire path of the node that declares the tag, with real instance ids. */
  markerPath: string;
  /** The declared tag, verbatim - including the `tag:subfield` compound form. */
  pathKey: string;
}

/**
 * Resolve the `__pathKey` marker for a wire path by walking the form structure.
 *
 * The form structure is the single source of truth for which stored paths carry
 * a `pathKey` tag (`state: { pathKey: 'region' }` and friends). Until #238 the
 * markers were FormControls that FormGenerator added beside each tagged leaf
 * and the save payload picked up from the form; any `FormGroup.reset()` nulled
 * them and the rows written in that session were stored untagged. Deriving the
 * marker from the structure at save time means it cannot be lost: if the
 * structure declares a tag for a path, the payload carries it.
 *
 * The walk follows the structure, not the path: a FormGroup descends by key, a
 * FormArray consumes the next segment as the instance id (`id-<uuid>`, or a
 * legacy numeric index) and descends into its item structure, a FormControl
 * ends the walk. Segments the structure does not know (`_meta`, `.value`,
 * `.name`, `.id` under an object-valued control) also end it. A tag is read
 * from FormControl nodes only - the only place FormGenerator ever added a
 * marker - and its wire path (ids included) is the marker path. The server's
 * extractPathKeys / backFillPathKeys then lands the tag on the right leaf
 * exactly as it does today, so this is byte-compatible with the markers
 * FormGenerator used to emit.
 *
 * Cost: one structure lookup per path segment. A sparse save resolves only its
 * changed paths; a FormArray is never iterated, so its item count is irrelevant.
 */
export function resolvePathKeyMarker(path: string, structure: FormStructure): PathKeyMarker | null {
  const segments = path.split('.');
  const consumed: string[] = [];
  let node: FormStructure | undefined = structure;

  for (let i = 0; i < segments.length && node; i++) {
    const segment = segments[i];
    // Own keys only, so a segment like 'constructor' can't resolve to Object.prototype.
    const item: StructureNode | undefined = Object.prototype.hasOwnProperty.call(node, segment)
      ? node[segment]
      : undefined;

    if (!item) {
      return null;
    }

    consumed.push(segment);

    if (item.type === FormStructureType.FormControl) {
      // FormControls only (the old FormGenerator rule). A tag on a FormGroup or
      // FormArray would make the server's last backfill step (LIKE 'T.%') tag
      // every untagged row beneath it.
      return typeof item.pathKey === 'string' && item.pathKey
        ? { markerPath: consumed.join('.'), pathKey: item.pathKey }
        : null;
    }

    if (item.type === FormStructureType.FormGroup) {
      node = item.value;
    } else if (item.type === FormStructureType.FormArray) {
      // The next segment is the instance id, whatever shape it takes.
      i++;
      if (i >= segments.length) {
        return null;
      }
      consumed.push(segments[i]);
      node = item.value;
    } else {
      return null;
    }
  }

  return null;
}

/**
 * Add a `<markerPath>__pathKey` entry for every flat wire path in `paths` whose
 * structure node declares a tag. Writes into `target` (a flat path -> value map)
 * and leaves existing entries alone.
 */
export function addPathKeyMarkersForPaths(
  paths: Iterable<string>,
  structure: FormStructure,
  target: { [path: string]: any }
): { [path: string]: any } {
  for (const path of paths) {
    if (path.endsWith(PATH_KEY_MARKER_SUFFIX)) {
      continue;
    }

    const marker = resolvePathKeyMarker(path, structure);

    if (marker) {
      const key = marker.markerPath + PATH_KEY_MARKER_SUFFIX;
      if (!(key in target)) {
        target[key] = marker.pathKey;
      }
    }
  }

  return target;
}
